import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

const MARKETS = ["Monaco", "Geneva", "Paris", "Other"];
const SERVICES = [
  "Retained Executive Search",
  "Talent Development",
  "Talent Strategy Consulting",
  "The Beyond Partnership",
  "Not sure yet",
];

const inputClass =
  "h-12 rounded-[var(--radius-sm)] border-[color:var(--bp-border)] bg-[color:var(--bp-bg-2)] text-[color:var(--bp-cream)] placeholder:text-[color:var(--bp-text-muted)]/60 focus-visible:ring-2 focus-visible:ring-[color:var(--bp-teal)] focus-visible:ring-offset-0";
const labelClass = "text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bp-text-muted)]";

export const LeadForm = ({ type = "booking", onSuccess, compact = false, submitLabel = "Request your confidential briefing" }) => {
  const [form, setForm] = useState({
    name: "", email: "", role: "", institution: "", market: "", service: "", message: "", consent: false,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email so we can respond.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm you consent to be contacted.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.createLead({ type, ...form });
      toast.success("Request received", { description: "We respond to confidential briefings within four business hours." });
      setForm({ name: "", email: "", role: "", institution: "", market: "", service: "", message: "", consent: false });
      onSuccess && onSuccess(res);
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again, or email contact@beyondpotentials.com." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="lead-form">
      <div className={cn("grid gap-5", compact ? "sm:grid-cols-1" : "sm:grid-cols-2")}>
        <div className="space-y-2">
          <Label className={labelClass} htmlFor="lf-name">Full name</Label>
          <Input id="lf-name" data-testid="lead-form-name-input" className={inputClass} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
        </div>
        <div className="space-y-2">
          <Label className={labelClass} htmlFor="lf-email">Work email</Label>
          <Input id="lf-email" data-testid="lead-form-email-input" type="email" className={inputClass} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@institution.com" />
        </div>
      </div>
      <div className={cn("grid gap-5", compact ? "sm:grid-cols-1" : "sm:grid-cols-2")}>
        <div className="space-y-2">
          <Label className={labelClass} htmlFor="lf-role">Role / Title</Label>
          <Input id="lf-role" data-testid="lead-form-role-input" className={inputClass} value={form.role} onChange={(e) => set("role", e.target.value)} placeholder="e.g. Head of HR" />
        </div>
        <div className="space-y-2">
          <Label className={labelClass} htmlFor="lf-institution">Institution</Label>
          <Input id="lf-institution" data-testid="lead-form-institution-input" className={inputClass} value={form.institution} onChange={(e) => set("institution", e.target.value)} placeholder="Your institution" />
        </div>
      </div>
      <div className={cn("grid gap-5", compact ? "sm:grid-cols-1" : "sm:grid-cols-2")}>
        <div className="space-y-2">
          <Label className={labelClass}>Market</Label>
          <Select value={form.market} onValueChange={(v) => set("market", v)}>
            <SelectTrigger data-testid="lead-form-market-select" className={cn(inputClass, "data-[placeholder]:text-[color:var(--bp-text-muted)]/60")}>
              <SelectValue placeholder="Select market" />
            </SelectTrigger>
            <SelectContent>
              {MARKETS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className={labelClass}>Service interest</Label>
          <Select value={form.service} onValueChange={(v) => set("service", v)}>
            <SelectTrigger data-testid="lead-form-service-select" className={cn(inputClass, "data-[placeholder]:text-[color:var(--bp-text-muted)]/60")}>
              <SelectValue placeholder="Select service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label className={labelClass} htmlFor="lf-message">Your talent challenge</Label>
        <Textarea id="lf-message" data-testid="lead-form-message-input" rows={compact ? 3 : 4} className={cn(inputClass, "h-auto py-3")} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us briefly what you're looking to solve. Everything you share is treated in confidence." />
      </div>
      <label className="flex cursor-pointer items-start gap-3 text-[13px] leading-relaxed text-[color:var(--bp-text-muted)]">
        <Checkbox data-testid="lead-form-consent-checkbox" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5 border-[color:var(--bp-border)] data-[state=checked]:bg-[color:var(--bp-teal)] data-[state=checked]:border-[color:var(--bp-teal)]" />
        <span>I consent to Beyond Potentials contacting me about my enquiry. We never share your details without your permission.</span>
      </label>
      <button
        type="submit"
        disabled={loading}
        data-testid="lead-form-submit-button"
        className="group inline-flex w-full items-center justify-center gap-2 rounded-[var(--radius-sm)] bg-[color:var(--bp-teal)] px-6 py-4 text-sm font-semibold text-[color:var(--bp-bg)] shadow-[var(--shadow-soft)] transition-shadow hover:shadow-[var(--shadow-lift)] disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {loading ? "Sending…" : submitLabel}
        {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
      </button>
      <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] uppercase tracking-[0.12em] text-[color:var(--bp-text-muted)]/70">
        <span>No obligation</span><span aria-hidden>·</span><span>100% confidential</span><span aria-hidden>·</span><span>Reply within 4 business hours</span>
      </p>
    </form>
  );
};
